/**
 * Хук для инициализации виджета
 */

import { useEffect, useRef } from 'react';
import { useChatStore } from '@/store/chatStore';
import { useConfigStore } from '@/store/configStore';
import { ApiService } from '@/services/api';
import {
  fetchChatSettings,
  getContactsForLanguage,
  getWelcomeMessage,
  mapQuickQuestions,
  resolveLanguageFromLocation,
  getFallbackMessage,
  isSupportedLanguage,
} from '@/services/api/settings.api';
import type { ContactsConfig, SupportedLanguage } from '@/types/config.types';
import { chatLogger } from '@/utils/chatLogger';

const FALLBACK_WELCOME: Record<SupportedLanguage, string> = {
  ru: 'Здравствуйте! Я виртуальный ассистент LARTE Design.\nПомогу подобрать обвес и комплектацию под ваш автомобиль, рассказать про материалы и реальные проекты, а также рассчитать стоимость и сроки.\nВыберите, с чего начнём:',
  en: 'Hello! I\'m the virtual assistant of LARTE Design.\nI\'ll help you choose a body kit for your car, tell you about materials and real projects, and calculate the cost and delivery time.\nChoose where to start:',
  de: 'Hallo! Ich bin der virtuelle Assistent von LARTE Design.\nIch helfe Ihnen, ein Bodykit für Ihr Auto auszuwählen, erzähle Ihnen von Materialien und realen Projekten und berechne Kosten und Lieferzeiten.\nWählen Sie, womit wir beginnen:',
  fr: 'Bonjour ! Je suis l\'assistant virtuel de LARTE Design.\nJe vous aide à choisir un kit carrosserie pour votre voiture, vous parle des matériaux et des projets réels, et calcule le coût et les délais.\nChoisissez par où commencer :',
};

const FALLBACK_QUICK_QUESTIONS: Record<SupportedLanguage, string[]> = {
  ru: ['Подобрать комплект для моего авто', 'Узнать цену и сроки', 'Материалы и производство', 'Реальные проекты и фото', 'Доставка', 'Задать другой вопрос'],
  en: ['Find a kit for my car', 'Get price and delivery time', 'Materials and manufacturing', 'Real projects and photos', 'Shipping', 'Ask another question'],
  de: ['Kit für mein Auto finden', 'Preis und Lieferzeit erfahren', 'Materialien und Fertigung', 'Reale Projekte und Fotos', 'Lieferung', 'Andere Frage stellen'],
  fr: ['Trouver un kit pour ma voiture', 'Prix et délais', 'Matériaux et fabrication', 'Projets réels et photos', 'Livraison', 'Poser une autre question'],
};

/**
 * Хук для инициализации виджета
 * 
 * @description Загружает параметры из Dify API или инициализирует n8n режим:
 * - opening_statement (приветствие)
 * - suggested_questions (начальные вопросы)
 * - file_upload настройки
 */
export function useInitialization() {
  const { config } = useConfigStore();
  const { setWelcomeMessage, setInitialQuestions, loadFromStorage } = useChatStore();
  const isInitializedRef = useRef(false);

  useEffect(() => {
    // Выполняем инициализацию только один раз
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    const initialize = async () => {
      try {
        // 1. Загружаем данные из localStorage
        loadFromStorage();

        const supabaseUrl = config.supabaseUrl || import.meta.env.VITE_SUPABASE_URL;
        const supabaseAnonKey = config.supabaseAnonKey || import.meta.env.VITE_SUPABASE_ANON_KEY;
        const hasSupabaseBootstrap = !!(supabaseUrl && supabaseAnonKey);

        // 2. Определяем режим работы (n8n или Dify)
        const isN8nMode = !config.apiKey || !config.apiKey.startsWith('app-');
        
        // 3. Проверяем наличие endpoints только если нет bootstrap через Supabase
        if (!config.baseURL && !config.chatEndpoint && !hasSupabaseBootstrap) {
          chatLogger.warn('[Initialization] baseURL не настроен');
        }

        chatLogger.log('[Initialization] Режим:', isN8nMode ? 'n8n' : 'Dify');

        // 4. Пытаемся загрузить настройки из Supabase (если есть ключи)
        if (supabaseUrl && supabaseAnonKey) {
          // Сохраняем ключи в config store для дочерних сервисов (reviews API и т.д.)
          if (!config.supabaseUrl || !config.supabaseAnonKey) {
            useConfigStore.getState().setConfig({ supabaseUrl, supabaseAnonKey });
          }

          const settings = await fetchChatSettings(supabaseUrl, supabaseAnonKey);
          if (settings) {
            // Язык: приоритет - явный window config, иначе домен/путь
            const rawLang = (window as any).LarteChat?.language as string | undefined;
            const explicitLang = isSupportedLanguage(rawLang) ? rawLang : undefined;
            const resolvedLanguage = (explicitLang ||
              (resolveLanguageFromLocation(settings, window.location, 'en') as SupportedLanguage) ||
              'en') as SupportedLanguage;

            // Применяем язык
            useConfigStore.getState().setConfig({ language: resolvedLanguage });
            useConfigStore.getState().setLanguage(resolvedLanguage);

            // Контакты для языка
            const c = getContactsForLanguage(settings, resolvedLanguage);
            const contacts: ContactsConfig = {
              phone: c.phone,
              phoneLink: c.phone_link,
              whatsapp: c.whatsapp,
              telegram: c.telegram,
              email: c.email,
              website: c.website,
            };
            useConfigStore.getState().setContacts(contacts);

            // n8n endpoint + apiKey + custom translations
            const endpointBase = (() => {
              if (settings.api_endpoint) return settings.api_endpoint;
              if (!settings.chat_endpoint) return config.baseURL;
              try {
                return new URL(settings.chat_endpoint).origin;
              } catch {
                return config.baseURL;
              }
            })();

            const configUpdate: Partial<typeof config> = {
              baseURL: endpointBase,
              apiKey: settings.api_key || config.apiKey,
              showContacts: settings.show_contacts,
              showQuickQuestions: settings.show_quick_questions,
              enableFileUpload: settings.enable_file_upload,
              callbackEndpoint: settings.callback_endpoint,
              chatEndpoint: settings.chat_endpoint,
              fallbackMessages: {
                ru: settings.fallback_message_ru,
                en: settings.fallback_message_en,
                de: settings.fallback_message_de,
              },
            };

            if (settings.translations && Object.keys(settings.translations).length > 0) {
              configUpdate.translations = settings.translations as any;
            }

            useConfigStore.getState().setConfig(configUpdate);

            // Re-apply translations for the resolved language (merges custom over defaults)
            useConfigStore.getState().setLanguage(resolvedLanguage);

            // Приветствие + быстрые вопросы
            const wm =
              getWelcomeMessage(settings, resolvedLanguage) ||
              getFallbackMessage(settings, resolvedLanguage) ||
              FALLBACK_WELCOME[resolvedLanguage];
            setWelcomeMessage(wm);

            const qq = mapQuickQuestions(settings.quick_questions || [], resolvedLanguage).filter((q) => q.text);
            if (settings.show_quick_questions && qq.length > 0) {
              setInitialQuestions(qq.map((q) => q.text));
            } else {
              setInitialQuestions(FALLBACK_QUICK_QUESTIONS[resolvedLanguage] || FALLBACK_QUICK_QUESTIONS.en);
            }

            // onReady и выходим
            config.onReady?.();
            return;
          }
        }

        // 6. Опционально: загружаем параметры из Dify (только для Dify режима)
        if (!isN8nMode && config.apiKey && config.baseURL) {
          try {
            const api = new ApiService({
              baseURL: config.baseURL,
              apiKey: config.apiKey,
              debug: config.debug,
            });
            
            const parameters = await api.chat.getParameters();
            
            chatLogger.log('[Initialization] Параметры Dify:', parameters);
          } catch (apiError) {
            chatLogger.warn('[Initialization] Не удалось загрузить параметры Dify:', apiError);
          }
        }

        // 7. Фолбэк если Supabase/Dify не дали приветствие
        const fallbackLang = (window as any).LarteChat?.language as SupportedLanguage | undefined || 'en';
        setWelcomeMessage(FALLBACK_WELCOME[fallbackLang] || FALLBACK_WELCOME.en);
        setInitialQuestions(FALLBACK_QUICK_QUESTIONS[fallbackLang] || FALLBACK_QUICK_QUESTIONS.en);

        // Запишем базовые fallbackMessages и endpoints по умолчанию
        useConfigStore.getState().setConfig({
          callbackEndpoint: config.callbackEndpoint || 'https://ai-api.larte-design.com/webhook/lead-capture',
          chatEndpoint: config.chatEndpoint || 'https://ai-api.larte-design.com/webhook/chat',
          fallbackMessages: {
            ...config.fallbackMessages,
            [fallbackLang]: config.fallbackMessages?.[fallbackLang] || FALLBACK_WELCOME[fallbackLang],
          },
        });

        // Вызываем onReady
        config.onReady?.();

        chatLogger.log('[Initialization] Виджет инициализирован', {
          language: config.language,
          userId: useChatStore.getState().userId,
        });
      } catch (error) {
        chatLogger.error('[Initialization] Критическая ошибка инициализации:', error);
        
        // Все равно вызываем onReady
        config.onReady?.();
      }
    };

    initialize();
  }, [config, setWelcomeMessage, setInitialQuestions, loadFromStorage]);
}

